import { useState } from 'react';
import { useStore } from 'react-redux';
import type { RootState } from '@/store';
import { useToast } from '@/shared/providers';
import { registrarAcao } from '@/shared/utils/reportAction';
import { reportError } from '@/shared/utils/reportError';
import { useAppSelector } from '@/store/hooks';
import { comandosDaSuperficie, FalhaDaSimulacao } from '../store/services';
import { selectSimulacaoAtual, selectSuperficieDaSimulacao } from '../store/selectors';
import type { AcessoAosArquivos } from '../types/hookTypes';
import { classificarLink } from '../utils/classificarLink';

/**
 * Hook de efeito de navegador (exceção escrita da regra #1): a URL de 60 s é credencial de
 * leitura e não passa pelo Redux (CA-18). Cada clique pede uma URL nova; abrir não gera evento ao
 * motor.
 *
 * A aba sai no próprio clique, antes do `await`: aberta depois dele, o bloqueador de pop-up a
 * recusa. Sem aba (bloqueada mesmo assim), a URL abre pela via normal.
 */
export function useAcessarArquivoDaSimulacao(): AcessoAosArquivos {
  const store = useStore<RootState>();
  const simulacaoId = useAppSelector(selectSimulacaoAtual)?.id ?? null;
  const superficie = useAppSelector(selectSuperficieDaSimulacao);
  const toast = useToast();
  const [indisponiveis, setIndisponiveis] = useState<string[]>([]);
  const marcarIndisponivel = (arquivoId: string) => {
    setIndisponiveis(atuais => atuais.includes(arquivoId) ? atuais : [...atuais, arquivoId]);
    registrarAcao('negociacaoSimulacao.arquivoIndisponivel', {
      simulacaoId,
      arquivoId
    });
  };
  const abrir = async (arquivoId_0: string) => {
    if (!simulacaoId) return;
    const aba = window.open('', '_blank');
    if (aba) aba.opener = null;
    try {
      const {
        url
      } = await comandosDaSuperficie(superficie).acessarArquivo(simulacaoId, arquivoId_0);
      if (store.getState().negociacaoSimulacao.simulacao?.id !== simulacaoId) {
        aba?.close();
        return;
      }
      const destino = classificarLink(url);
      if (!destino?.safe) throw new Error('URL de arquivo da simulação fora de https');
      if (aba) aba.location.href = destino.href;else window.open(destino.href, '_blank', 'noopener,noreferrer');
    } catch (erro) {
      aba?.close();
      if (store.getState().negociacaoSimulacao.simulacao?.id !== simulacaoId) return;
      if (erro instanceof FalhaDaSimulacao && erro.erro.status === 404) {
        marcarIndisponivel(arquivoId_0);
        return;
      }
      reportError(erro instanceof Error ? erro : new Error('Falha ao abrir arquivo'), {
        source: 'negociacaoSimulacao',
        acao: 'acessarArquivo',
        simulacaoId,
        arquivoId: arquivoId_0
      });
      toast.showError('Não foi possível abrir o arquivo', 'Tente novamente em instantes.');
    }
  };
  return {
    abrir,
    indisponiveis,
    simulacaoId,
    superficie,
    marcarIndisponivel
  };
}